import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { Menu, X } from 'lucide-react';

const Navbar = ({ showAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get current language suffix for navigation
  const getLangSuffix = () => {
    if (i18n.language === 'ru') return '-ru';
    if (i18n.language === 'kz') return '-kz';
    return '';
  };

  const navigateWithLang = (path) => {
    navigate(path + getLangSuffix());
    setMobileMenuOpen(false);
  };

  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById('how-it-works');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    setMobileMenuOpen(false);
  };

  // Get language-specific About URL
  const getAboutUrl = () => {
    if (i18n.language === 'ru') return 'https://methodmate.ai/ru/mainru/';
    if (i18n.language === 'kz') return 'https://methodmate.ai/kk/';
    return 'https://methodmate.ai/';
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_giftcard-vault-1/artifacts/03yh11a7_MethodMate%20Main%20Logo%20%281%29.png" 
              alt="MethodMate" 
              className="h-10 md:h-12"
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => navigateWithLang('/browse')}
              className="text-gray-700 hover:text-[#1E88E5] font-medium transition-colors"
            >
              {t('nav.browseGiftCards')}
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="text-gray-700 hover:text-[#1E88E5] font-medium transition-colors"
            >
              {t('nav.howItWorks')}
            </button>
            <a
              href={getAboutUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-[#1E88E5] font-medium transition-colors"
            >
              {t('nav.aboutMethodMate')}
            </a>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-[#1E88E5] p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Slide-out Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <button
              onClick={() => navigateWithLang('/browse')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-[#1E88E5] font-medium transition-colors rounded-lg"
            >
              {t('nav.browseGiftCards')}
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-[#1E88E5] font-medium transition-colors rounded-lg"
            >
              {t('nav.howItWorks')}
            </button>
            <a
              href={getAboutUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-[#1E88E5] font-medium transition-colors rounded-lg"
            >
              {t('nav.aboutMethodMate')}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
